import type { GrantOpportunityTask, GrantOpportunityTaskKind } from "@/api/grant_opportunity_tasks";

export type GrantTaskExtractionFocus = "reporting" | "milestone" | "compliance";

export type GrantTaskExtractionStatus = "pending" | "processing" | "completed" | "failed";

export type GrantTaskProposal = {
  id: string;
  title: string;
  kind: GrantOpportunityTaskKind;
  due_date: string | null;
  due_text: string | null;
  source_quote: string | null;
};

export type GrantTaskExtraction = {
  id: number;
  grant_opportunity_id: number;
  document_id: number;
  document_name: string;
  focus: GrantTaskExtractionFocus;
  status: GrantTaskExtractionStatus;
  current_stage: string | null;
  error_message: string | null;
  confirmed_at: string | null;
  started_at: string | null;
  last_heartbeat_at: string | null;
  created_at: string;
  proposals?: GrantTaskProposal[];
};

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

function extractionsPath(municipalityId: number, opportunityId: number): string {
  return `/api/v1/municipalities/${municipalityId}/grant_opportunities/${opportunityId}/task_extractions`;
}

export type GrantTaskExtractionSummary = {
  id: number;
  document_id: number;
  focus: GrantTaskExtractionFocus;
  status: GrantTaskExtractionStatus;
  confirmed_at: string | null;
  created_at: string;
};

export async function listGrantTaskExtractions(
  municipalityId: number,
  opportunityId: number,
): Promise<GrantTaskExtractionSummary[]> {
  const response = await fetch(extractionsPath(municipalityId, opportunityId), {
    credentials: "include",
    headers: jsonHeaders,
  });
  if (!response.ok) {
    throw new Error("Failed to load extraction history");
  }
  return response.json();
}

export async function createGrantTaskExtraction(
  municipalityId: number,
  opportunityId: number,
  payload: { document_id: number; focus?: GrantTaskExtractionFocus },
): Promise<GrantTaskExtraction> {
  const response = await fetch(extractionsPath(municipalityId, opportunityId), {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.errors?.join(", ") ?? "Failed to start extraction");
  }
  return response.json();
}

export async function getGrantTaskExtraction(
  municipalityId: number,
  opportunityId: number,
  extractionId: number,
): Promise<GrantTaskExtraction> {
  const response = await fetch(`${extractionsPath(municipalityId, opportunityId)}/${extractionId}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error("Failed to load extraction");
  }
  return response.json();
}

export class GrantTaskExtractionConflictError extends Error {}

export async function confirmGrantTaskExtraction(
  municipalityId: number,
  opportunityId: number,
  extractionId: number,
  items: Array<{ title: string; kind: GrantOpportunityTaskKind; due_at: string | null }>,
): Promise<GrantOpportunityTask[]> {
  const response = await fetch(
    `${extractionsPath(municipalityId, opportunityId)}/${extractionId}/confirm`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ items }),
    },
  );
  if (response.status === 409) {
    throw new GrantTaskExtractionConflictError("These tasks have already been added");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.errors?.join(", ") ?? "Failed to add tasks");
  }
  return response.json();
}
